import React, { useState, useEffect } from "react";
import Select from "react-select";
import { Link } from "react-router-dom";
import { State } from "../../common/selectOption/selectOption";
import { useAddUserMutation, useUpdateUserMutation } from "../../redux/api/userApi";
import * as Yup from "yup";
import { useFormik } from "formik";

const userSchema = Yup.object().shape({
  userName: Yup.string().required('User Name is required'),
  mobile: Yup.string()
    .required('Mobile Number is required')
    .matches(
      /^[6-9]\d{9}$/,
      'Mobile Number must be a valid 10-digit Indian mobile number'
    ),
  email: Yup.string()
    .required("Email is required")
    .email('Invalid email address'),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters"),
  confirmPassword: Yup.string()
    .required("Please confirm your password")
    .oneOf([Yup.ref('password'), null], 'Passwords must match'),
  city: Yup.string(),
  state: Yup.string(),
  address: Yup.string(),
  postalCode: Yup.string(),
  status: Yup.boolean().default(true)
});

const UserModal = ({ isEdit = false, userData = null, onSuccess = () => {} }) => {
  const [addUser] = useAddUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      userName: '',
      email: '',
      mobile: '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      password: '',
      confirmPassword: '',
      status: true
    },
    validationSchema: userSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        // Remove confirmPassword before sending to API
        const { confirmPassword, ...userData } = values;
        console.log("userdata", userData);
        if (isEdit && userData) {
          await updateUser({ id: userData._id, ...userData }).unwrap();
        } else {
          await addUser(userData).unwrap();
        }
        
        onSuccess();
        resetForm();
      } catch (error) {
        console.error('Error submitting user:', error);
      } finally {
        setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    if (isEdit && userData) {
      formik.setValues({
        userName: userData.userName || '',
        email: userData.email || '',
        mobile: userData.mobile || '',
        address: userData.address || '',
        city: userData.city || '',
        state: userData.state || '',
        postalCode: userData.postalCode || '',
        password: '', // Don't pre-fill password for security
        confirmPassword: '', // Don't pre-fill confirm password
        status: userData.status !== undefined ? userData.status : true
      });
    }
  }, [isEdit, userData]);

  const handleStatusChange = (e) => {
    formik.setFieldValue('status', e.target.checked);
  };

  return (
    <div>
      {/* Add User */}
      <div className="modal fade" id="add-user">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div className="page-title">
                <h4>{isEdit ? 'Edit User' : 'Add User'}</h4>
              </div>
              <button
                type="button"
                className="close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => formik.resetForm()}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <form onSubmit={formik.handleSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="mb-3">
                      <label className="form-label">
                        User Name <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className={`form-control ${formik.touched.userName && formik.errors.userName ? 'is-invalid' : ''}`}
                        name="userName"
                        value={formik.values.userName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.userName && formik.errors.userName && (
                        <div className="invalid-feedback">{formik.errors.userName}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Email <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="email" 
                        className={`form-control ${formik.touched.email && formik.errors.email ? 'is-invalid' : ''}`}
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.email && formik.errors.email && (
                        <div className="invalid-feedback">{formik.errors.email}</div>
                      )}
                    </div>
                  </div>
                  {!isEdit && (
                    <>
                      <div className="col-lg-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Password <span className="text-danger">*</span>
                          </label>
                          <div className="input-group">
                            <input 
                              type={showPassword ? "text" : "password"} 
                              className={`form-control ${formik.touched.password && formik.errors.password ? 'is-invalid' : ''}`}
                              name="password"
                              value={formik.values.password}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                            <button 
                              className="btn btn-outline-secondary" 
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? 'Hide' : 'Show'}
                            </button>
                          </div>
                          {formik.touched.password && formik.errors.password && (
                            <div className="invalid-feedback">{formik.errors.password}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Confirm Password <span className="text-danger">*</span>
                          </label>
                          <input 
                            type={showPassword ? "text" : "password"} 
                            className={`form-control ${formik.touched.confirmPassword && formik.errors.confirmPassword ? 'is-invalid' : ''}`}
                            name="confirmPassword"
                            value={formik.values.confirmPassword}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                            <div className="invalid-feedback">{formik.errors.confirmPassword}</div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  <div className="col-lg-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Mobile <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className={`form-control ${formik.touched.mobile && formik.errors.mobile ? 'is-invalid' : ''}`}
                        name="mobile"
                        value={formik.values.mobile}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.mobile && formik.errors.mobile && (
                        <div className="invalid-feedback">{formik.errors.mobile}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Address
                      </label>
                      <input 
                        type="text" 
                        className={`form-control ${formik.touched.address && formik.errors.address ? 'is-invalid' : ''}`}
                        name="address"
                        value={formik.values.address}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.address && formik.errors.address && (
                        <div className="invalid-feedback">{formik.errors.address}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">City</label>
                      <input 
                        type="text" 
                        className="form-control"
                        name="city"
                        value={formik.values.city}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">State</label>
                      <Select
                        options={State}
                        classNamePrefix="select"
                        name="state"
                        value={State.find(option => option.value === formik.values.state)}
                        onChange={(option) => formik.setFieldValue('state', option.value)}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Postal Code</label>
                      <input 
                        type="text" 
                        className="form-control"
                        name="postalCode"
                        value={formik.values.postalCode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-0">
                      <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                        <span className="status-label">Status</span>
                        <input
                          type="checkbox"
                          id="user-status"
                          className="check"
                          checked={formik.values.status}
                          onChange={handleStatusChange}
                        />
                        <label htmlFor="user-status" className="checktoggle mb-0" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                  data-bs-dismiss="modal"
                  onClick={() => formik.resetForm()}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary fs-13 fw-medium p-2 px-3"
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? 'Processing...' : isEdit ? 'Update User' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* Delete Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content p-5">
            <div className="modal-body text-center p-0">
              <span className="rounded-circle d-inline-flex p-2 bg-danger-transparent mb-2">
                <i className="ti ti-trash fs-24 text-danger" />
              </span>
              <h4 className="fs-20 text-gray-9 fw-bold mb-2 mt-1">
                Delete User
              </h4>
              <p className="text-gray-6 mb-0 fs-16">
                Are you sure you want to delete user?
              </p>
              <div className="d-flex justify-content-center mt-3">
                <Link
                  className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </Link>
                <Link
                  to="#"
                  className="btn btn-primary fs-13 fw-medium p-2 px-3"
                  data-bs-dismiss="modal"
                >
                  Yes Delete
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserModal;